import { createStore, Store, Unsubscribe } from "redux";
import { ApiCalls, getVariation } from "../constants/ApiCalls";
import { DocType, orderDocs } from "../constants/DocType";
import { ApiErrors } from "../constants/Errors";
import { FlowStates } from "../constants/FlowStates";
import { Logger } from "../constants/Logger";
import { PubSubEvents } from "../constants/PubSubEvents";
import { Routes } from "../constants/Routing";
import {
  DOC_CAPTURE_EXPERIMENT_VARIATION,
  DOC_CAPTURE_LOCAL_DECODE_EXPERIMENT,
} from "../constants/Experiments";

export enum ActionTypes {
  SET_DOCUMENTS = "SET_DOCUMENTS",
  SET_LOCAL_DECODE = "SET_LOCAL_DECODE",
}

export interface DcState {
  documents?: DocType[];
  enableLocalDecode?: boolean;
}

export interface DcAction extends DcState {
  type: string;
}

export class DcStore {
  private store: Store;

  constructor() {
    this.store = DcStore.createNewStore();
  }

  private static createNewStore = (): Store => createStore(
    DcStore.reducer,
    {},
  );

  // todo maybe these two can be gained by extending store
  public subscribe = (listener: () => void): Unsubscribe => {
    return this.store.subscribe(listener);
  };

  public getState = (): DcState => this.store.getState();

  public storeActions = {
    setDocuments: (documents: DocType[] | undefined) => {
      // force docs to be stored in correct order
      if (!!documents) {
        documents = orderDocs(documents);
      }
      this.store.dispatch({
        documents,
        type: ActionTypes.SET_DOCUMENTS,
      });
    },
    setEnableLocalDecode: (enableLocalDecode: boolean) => {
      this.store.dispatch({
        enableLocalDecode,
        type: ActionTypes.SET_LOCAL_DECODE,
      });
    }
  };

  public apiActions = {
    getReqDocs: (uuid: string | undefined, appId: string | undefined, coBorrower: string | null,
                 submitMetric: boolean, documentType: string | null, useDefault: boolean) => {
      return ApiCalls.getRequiredDocs(
        uuid,
        appId,
        coBorrower,
        submitMetric,
        documentType,
        useDefault,
        (documents) => {
          if (documents.length === 0) {
            Routes.goToFinished();
            return;
          }
          this.storeActions.setDocuments(documents);
        },
        (error: string, data: any) => {
          switch (error) {
            case ApiErrors.ANON_ID_NOT_FOUND: {
              PubSubEvents.setFlowState(FlowStates.UUID_NOT_FOUND);
              break;
            }
            case ApiErrors.ANON_ID_EXPIRED: {
              Routes.expiredLinkLoginRedirect(data.appId, data.coborrower);
              break;
            }
            case ApiErrors.LOGGED_OUT: {
              PubSubEvents.setFlowState(FlowStates.LOGGED_OUT);
              break;
            }
            default: {
              Logger.error(`Encountered error getting required docs: ${error}`);
              PubSubEvents.setFlowState(FlowStates.MARBLES);
            }
          }
        },
      );
    },
    getEnableLocalDecode: (uuid: string | undefined, appId: string | undefined) => {
      this.getLocalDecodeVariation(uuid, appId);
    }
  };

  private async getLocalDecodeVariation(uuid: string | undefined, appId: string | undefined) {
    const localDecodeExperiment = await getVariation(uuid, appId, DOC_CAPTURE_LOCAL_DECODE_EXPERIMENT);
    const enableLocalDecode = (localDecodeExperiment === DOC_CAPTURE_EXPERIMENT_VARIATION);
    Logger.info(`Local decode enabled: ${enableLocalDecode} with variation: ${localDecodeExperiment}`);
    this.storeActions.setEnableLocalDecode(enableLocalDecode);
  }

  private static reducer(state: DcState, action: DcAction) {
    switch (action.type) {
      case ActionTypes.SET_DOCUMENTS: {
        return {
          ...state, documents: action.documents,
        };
      }
      case ActionTypes.SET_LOCAL_DECODE: {
        return {
          ...state, enableLocalDecode: action.enableLocalDecode,
        };
      }
      default: {
        return state;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/stores/DcStore.ts